import React, { useState, useEffect } from 'react';
import { User } from './AppContext';
import { Button } from './Components';

interface AdminPasswordModalProps {
  user: User | null;
  isOpen: boolean;
  onClose: () => void;
  onSavePassword: (userId: string, newPassword: string, sendResetEmail: boolean) => Promise<void>;
  onSendResetEmail: (email: string) => Promise<void>;
}

export const AdminPasswordModal: React.FC<AdminPasswordModalProps> = ({
  user,
  isOpen,
  onClose,
  onSavePassword,
  onSendResetEmail
}) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [sendEmailNotice, setSendEmailNotice] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSendingResetEmail, setIsSendingResetEmail] = useState(false);
  const [statusMessage, setStatusMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [temporaryCredentials, setTemporaryCredentials] = useState<{ pass: string; email: string } | null>(null);

  useEffect(() => {
    if (isOpen) {
      setNewPassword('');
      setConfirmPassword('');
      setStatusMessage(null);
      setTemporaryCredentials(null);
      setIsSubmitting(false);
      setIsSendingResetEmail(false);
    }
  }, [isOpen, user]);

  if (!isOpen || !user) return null;

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatusMessage(null);

    if (!newPassword) {
      setStatusMessage({ type: 'error', text: 'Please enter a new password.' });
      return;
    }
    if (newPassword.length < 6) {
      setStatusMessage({ type: 'error', text: 'Password must be at least 6 characters long.' });
      return;
    }
    if (newPassword !== confirmPassword) {
      setStatusMessage({ type: 'error', text: 'Password confirmation does not match.' });
      return;
    }

    setIsSubmitting(true);
    try {
      await onSavePassword(user.id, newPassword, sendEmailNotice);
      setStatusMessage({
        type: 'success',
        text: `Password successfully updated for ${user.name} (${user.email}).`
      });
      setTemporaryCredentials({ pass: newPassword, email: user.email });
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      setStatusMessage({
        type: 'error',
        text: err.message || 'Failed to update user password.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTriggerResetEmail = async () => {
    if (!user.email) {
      setStatusMessage({ type: 'error', text: 'User has no email address on file.' });
      return;
    }

    setIsSendingResetEmail(true);
    setStatusMessage(null);
    try {
      await onSendResetEmail(user.email);
      setStatusMessage({
        type: 'success',
        text: `Password reset instructions have been dispatched to ${user.email}.`
      });
    } catch (err: any) {
      setStatusMessage({
        type: 'error',
        text: err.message || 'Failed to send password reset email.'
      });
    } finally {
      setIsSendingResetEmail(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[650] flex items-center justify-center p-3 sm:p-5 md:p-6 bg-slate-950/70 backdrop-blur-md overflow-y-auto animate-in fade-in duration-200">
      <div 
        className="bg-white w-full max-w-xl rounded-[2rem] sm:rounded-[2.5rem] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col my-auto max-h-[90vh] border border-slate-100"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-5 sm:p-6 border-b border-slate-100 bg-slate-50/70 flex justify-between items-start gap-4 shrink-0">
          <div className="flex items-center gap-3.5">
            <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-2xl bg-indigo-50 text-indigo-700 flex items-center justify-center text-lg font-black shrink-0">
              <i className="fa-solid fa-key"></i>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-indigo-100/70 text-indigo-800 border border-indigo-200">
                  Security Administration
                </span>
                <span className={`text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full ${
                  user.role === 'doctor' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
                  user.role === 'admin' ? 'bg-purple-50 text-purple-700 border border-purple-200' :
                  'bg-blue-50 text-blue-700 border border-blue-200'
                }`}>
                  {user.role}
                </span>
              </div>
              <h3 className="text-lg sm:text-xl font-black text-slate-900 tracking-tight mt-1">
                Change Password for {user.name}
              </h3>
              <p className="text-slate-400 font-bold text-[11px] mt-0.5">
                {user.email} • UID: {user.id.slice(0, 10)}...
              </p>
            </div>
          </div>
          <button 
            onClick={onClose} 
            className="text-slate-400 hover:text-slate-600 transition-colors w-9 h-9 rounded-xl bg-white border border-slate-200 flex items-center justify-center shadow-xs shrink-0"
            title="Close"
          >
            <i className="fa-solid fa-xmark text-base"></i>
          </button>
        </div>

        {/* Content Body */}
        <div className="p-5 sm:p-6 md:p-8 space-y-6 overflow-y-auto flex-1 scrollbar-thin min-h-0 bg-slate-50/30">
          {/* Status Alert Banner */}
          {statusMessage && (
            <div className={`p-4 rounded-2xl border text-xs font-bold flex items-start gap-3 animate-in fade-in duration-200 ${
              statusMessage.type === 'success' 
                ? 'bg-emerald-50 border-emerald-200 text-emerald-800' 
                : 'bg-rose-50 border-rose-200 text-rose-800'
            }`}>
              <i className={`mt-0.5 text-sm ${statusMessage.type === 'success' ? 'fa-solid fa-circle-check text-emerald-600' : 'fa-solid fa-triangle-exclamation text-rose-600'}`}></i>
              <div className="flex-1">
                <p className="font-black">{statusMessage.type === 'success' ? 'Operation Completed' : 'Action Failed'}</p>
                <p className="mt-0.5 leading-relaxed font-medium">{statusMessage.text}</p>
              </div>
            </div>
          )}

          {/* Temporary Credentials Copy Card */}
          {temporaryCredentials && (
            <div className="bg-indigo-50/70 border border-indigo-200 rounded-2xl p-4 space-y-2">
              <p className="text-[10px] font-black uppercase text-indigo-700 tracking-wider flex items-center gap-1.5">
                <i className="fa-solid fa-circle-info"></i> Temporary Credentials Generated
              </p>
              <div className="flex items-center justify-between bg-white p-3 rounded-xl border border-indigo-100">
                <div>
                  <p className="text-[10px] text-slate-400 font-bold uppercase">New Account Password</p>
                  <p className="text-sm font-black font-mono text-slate-900 mt-0.5">{temporaryCredentials.pass}</p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(temporaryCredentials.pass);
                  }}
                  className="px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-lg text-xs font-bold transition-all flex items-center gap-1"
                >
                  <i className="fa-regular fa-copy"></i> Copy
                </button>
              </div>
              <p className="text-[11px] text-indigo-900/70 font-medium">
                The user can sign in using these credentials or follow the reset link sent to {temporaryCredentials.email}.
              </p>
            </div>
          )}

          {/* Form: Set New Password Directly */}
          <form onSubmit={handleUpdatePassword} className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-2xs space-y-4">
            <div className="border-b border-slate-100 pb-3">
              <h4 className="text-sm font-black text-slate-900 flex items-center gap-2">
                <i className="fa-solid fa-lock text-indigo-600"></i> Set New Password Directly
              </h4>
              <p className="text-[11px] text-slate-500 font-medium mt-0.5">
                Assign a new password for this user immediately without waiting for an email response.
              </p>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-[10px] font-black uppercase tracking-wider text-slate-400 block mb-1">
                  New Password (min 6 characters)
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    className="w-full p-3.5 pr-10 bg-slate-50 rounded-xl border border-slate-200 focus:border-indigo-500 focus:bg-white outline-none font-bold text-sm text-slate-800 transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-3.5 text-slate-400 hover:text-slate-600"
                    title={showPassword ? "Hide password" : "Show password"}
                  >
                    <i className={`fa-solid ${showPassword ? 'fa-eye-slash' : 'fa-eye'} text-sm`}></i>
                  </button>
                </div>
              </div>

              <div>
                <label className="text-[10px] font-black uppercase tracking-wider text-slate-400 block mb-1">
                  Confirm New Password
                </label>
                <input
                  type={showPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-type new password"
                  className="w-full p-3.5 bg-slate-50 rounded-xl border border-slate-200 focus:border-indigo-500 focus:bg-white outline-none font-bold text-sm text-slate-800 transition-all"
                />
              </div>

              <label className="flex items-center gap-2.5 cursor-pointer pt-1">
                <input
                  type="checkbox"
                  checked={sendEmailNotice}
                  onChange={(e) => setSendEmailNotice(e.target.checked)}
                  className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500 border-slate-300"
                />
                <span className="text-xs font-bold text-slate-700">
                  Also dispatch password reset confirmation to {user.email}
                </span>
              </label>

              <Button
                type="submit"
                disabled={isSubmitting || !newPassword}
                className="w-full py-3 text-xs font-black uppercase tracking-wider shadow-sm flex items-center justify-center gap-2 mt-2"
              >
                {isSubmitting ? (
                  <>
                    <i className="fa-solid fa-circle-notch fa-spin"></i>
                    <span>Applying New Password...</span>
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-shield-check"></i>
                    <span>Save & Update User Password</span>
                  </>
                )}
              </Button>
            </div>
          </form>

          {/* Quick Option: Send Email Reset Link */}
          <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-2xs space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h4 className="text-sm font-black text-slate-900 flex items-center gap-2">
                  <i className="fa-solid fa-paper-plane text-emerald-600"></i> Or Send Self-Service Reset Link
                </h4>
                <p className="text-[11px] text-slate-500 font-medium mt-0.5">
                  Sends an official Firebase authentication password reset email directly to <strong>{user.email}</strong> so the user can choose their own new password.
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={handleTriggerResetEmail}
              disabled={isSendingResetEmail}
              className="w-full py-3 px-4 rounded-xl border border-slate-200 hover:border-indigo-300 bg-slate-50 hover:bg-indigo-50/50 text-slate-700 hover:text-indigo-700 font-bold text-xs transition-all flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {isSendingResetEmail ? (
                <>
                  <i className="fa-solid fa-circle-notch fa-spin text-indigo-600"></i>
                  <span>Dispatching Firebase Reset Email...</span>
                </>
              ) : (
                <>
                  <i className="fa-solid fa-envelope-circle-check text-indigo-600"></i>
                  <span>Send Reset Email to {user.email}</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 sm:p-5 bg-slate-50/90 backdrop-blur-md flex justify-end gap-3 shrink-0 border-t border-slate-100">
          <Button onClick={onClose} variant="ghost" className="py-2.5 px-5 text-xs font-bold">
            Done / Close
          </Button>
        </div>
      </div>
    </div>
  );
};
